<template>
  <div class="tableWithPerPageSelection">
    <div class="entete border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">{{ $t('listeNomenclatureType.title')}}</h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="is-pulled-right buttons boutonAction">
            <a
              class="button is-addOrEdit"
              @click="checkPermissions(permItem.maj)
                ? $router.push({ name: 'm.parametrage.nomenclature_type'})
                : showPopUp()"
              :class="{'button-access-no-permission': !checkPermissions(permItem.maj)}">
              {{ $t('listeNomenclatureType.actions.add')}}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="level mgt-20px">
      <div class="level-left">
        <div class="columns">
          <form @submit.prevent="setFilter(filterInput)" class="column is-12">
            <b-input
              v-model="filterInput"
              :placeholder="$t('general.button.search')"
              type="search"
              icon="magnify">
            </b-input>
          </form>
        </div>
      </div>
      <div class="level-right columns">
        <div class="column is-narrow">
          <a @click="displayInactif = !displayInactif">
            <span class="has-text-primary lien">
              {{ displayInactif
                ? $t('listeNomenclatureType.actions.archive', { nbre: getInactifNumber })
                : $t('listeNomenclatureType.actions.masquerArchiver', { nbre: getInactifNumber })
              }}
            </span>
          </a>
        </div>
      </div>
    </div>
    <v-client-table
      :data="listeFilter"
      :columns="columns"
      :options="options"
      ref="tableItems">
      <template slot='afterTable'>
        <table-items-in-view @input="$refs.tableItems.setLimit($event)"/>
      </template>
      <template #reference="{ row }">
        <div>{{ getReferenceTrad(row) }}</div>
      </template>
      <template #edit="{ row }">
        <div
          @click="checkPermissions(permItem.supprimer)
            ? $router.push({ name: 'm.parametrage.nomenclature_type', params: { id: row.id } })
            : showPopUp()"
          class="is-cursor-pointer">
          <b-icon
            :class="{'text-access-no-permission': !checkPermissions(permItem.maj)}"
            icon="pencil-box-outline"
            type="is-success">
          </b-icon>
        </div>
      </template>
      <template #archiver="{ row }">
        <archive-list-button
          :query="() => func.archiver({
            id: row.id,
            archiver: row.actif,
          })"
          :validation="func.validationNomenclature"
          :permissions="permItem.supprimer"
          :title="row.actif
            ? $t('listeNomenclatureType.popUpArchivage.titleArchiver')
            : $t('listeNomenclatureType.popUpArchivage.titleDesarchiver')"
          :successMessage="row.actif
            ? $t('listeNomenclatureType.messages.success.archiver')
            : $t('listeNomenclatureType.messages.success.desarchiver')"
          @done="loadItems(true)"
          :actif="row.actif"/>
      </template>
      <template #delete="{ row }">
        <delete-list-button
          :query="() => func.remove(row.id)"
          :validation="func.validationNomenclature"
          :permissions="permItem.supprimer"
          :title="$t('addEditNomenclatureType.delete.title')"
          :subtitle="$t('general.suppression.sousTitre')"
          :successMessage="$t('addEditNomenclatureType.delete.suppression')"
          @done="loadItems(true)"
          :tooltipLabel="$t('general.button.delete')"/>
      </template>
    </v-client-table>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import messageErreur from '@/services/general/messageErreur';
import validationNomenclature from '@/services/parametrage/validation/validationNomenclatureType';
import breadcrumb from '@/components/general/breadcrumb';
import TableItemsInView from '@/components/general/TableItemsInView';
import archiveListButton from '@/components/general/button/archiveListButton';
import deleteListButton from '@/components/general/button/deleteListButton';
import {
  TEXTS, RESULTS_PER_PAGE,
  NO_PER_PAGE_VALUE, SORT_ICON,
} from '@/configuration/clientTableConf';
import { NOMENCLATURE_TYPE } from '@/configuration/general/permissionsParametrageConf';
import { archiver, remove } from '@/api/nomenclatureType';
import { SET_FILTER } from '@/store/modules/nomenclatureType-mutation-type';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import { LISTE_REFERENCE_TYPE } from '@/configuration/parametrage/nomenclatureTypeConf';

const Parametres = createNamespacedHelpers('parametres');
const Permissions = createNamespacedHelpers('permissions');
const NomenclatureType = createNamespacedHelpers('nomenclatureType');

export default {
  name: 'ListeNomenclatureType',
  data() {
    return {
      filterInput: '',
      permItem: NOMENCLATURE_TYPE,
      displayInactif: true,
      func: {
        validationNomenclature,
        remove,
        archiver,
      },
    };
  },
  components: {
    appBreadcrumb: breadcrumb,
    TableItemsInView,
    archiveListButton,
    deleteListButton,
  },
  computed: {
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('general.breadcrumb.parametrage'), route: { name: 'l.parametrage.general' }, isActive: false },
        { title: this.$t('listeNomenclatureType.breadcrumb.liste'), route: { name: '' }, isActive: true },
      ];
    },
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...NomenclatureType.mapGetters({
      items: 'items',
      filter: 'filter',
    }),
    listeFilter() {
      const items = this.items.filter(item => item.libelle.toLowerCase()
        .includes(this.filter.toLocaleLowerCase() || this.filter));
      return items.filter(item => item.actif === this.displayInactif);
    },
    getInactifNumber() {
      return this.items.filter((item) => !item.actif).length;
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          libelle: this.$t('listeNomenclatureType.tab.libelle'),
          reference: this.$t('listeNomenclatureType.tab.reference'),
          edit: '',
          archiver: '',
          delete: '',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: TEXTS,
        columnsClasses: {
          edit: 'colonneSelected',
          archiver: 'colonneSelected',
          delete: 'colonneSelected',
        },
        sortable: ['libelle', 'reference'],
      };
    },
    columns() {
      return ['libelle', 'reference', 'edit', 'archiver', 'delete'];
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametreForce: 'loadParametreForce',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...NomenclatureType.mapActions({
      actionsloadItems: 'loadItems',
    }),
    ...NomenclatureType.mapMutations({
      setFilter: SET_FILTER,
    }),
    async loadItems(reloadStrore) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        await this.actionsloadItems();
        if (reloadStrore) {
          await this.loadParametreForce(PARAMETRES_KEYS.nomenclatureType);
        }
        loadingComponent.close();
      } catch (e) {
        messageErreur(this.$i18n);
        loadingComponent.close();
        throw (e);
      }
    },
    getReferenceTrad(item) {
      const find = LISTE_REFERENCE_TYPE.find(el => el.code === item.reference);
      return find ? this.$t(find.libelle) : '';
    },
  },
  async mounted() {
    this.filterInput = this.filter;
    this.setInformationPage(this.$t('pageParametrage.nomenclatureType.liste'));
    await this.loadItems();
  },
};

</script>
